import Anthropic from "@anthropic-ai/sdk";

export class AnthropicSTTService {
  private client: Anthropic;

  constructor(apiKey: string) {
    if (!apiKey) {
      throw new Error("Anthropic API key is required");
    }

    // Add warning about browser usage
    if (typeof window !== "undefined") {
      console.warn(
        "Warning: Using Anthropic API in the browser. Make sure you understand the security implications and have appropriate measures in place."
      );
    }

    this.client = new Anthropic({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true, // Required for browser usage
    });
  }

  async translateText(
    text: string
  ): Promise<{ text: string; duration: number }> {
    const startTime = Date.now();

    try {
      // Add basic input validation
      if (!text || typeof text !== "string") {
        throw new Error("Invalid input: text must be a non-empty string");
      }

      // Rate limiting and timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(
          () => reject(new Error("Translation request timed out")),
          30000
        );
      });

      const translationPromise = this.client.messages.create({
        model: "claude-3-7-sonnet-latest",
        max_tokens: 4096,
        messages: [
          {
            role: "user",
            content: `You are a professional translator. Translate the following text to English. Maintain the original meaning, tone, formality level, and any technical terminology. Only respond with the translation, no explanations or additional text:

${text}`,
          },
        ],
        temperature: 0.3,
      });

      // Race between timeout and translation
      const response = (await Promise.race([
        translationPromise,
        timeoutPromise,
      ])) as Awaited<typeof translationPromise>;

      // Get the first text content from the response
      const translatedText = response.content
        .filter((block) => block.type === "text")
        .map((block) => block.text)
        .join(" ")
        .trim();

      // Basic output validation
      if (!translatedText) {
        throw new Error("Translation returned empty result");
      }

      const duration = (Date.now() - startTime) / 1000;
      return {
        text: translatedText,
        duration,
      };
    } catch (error) {
      if (error instanceof Error) {
        // Sanitize error messages to avoid leaking sensitive information
        const safeErrorMessage = error.message.replace(
          new RegExp(String(this.client.apiKey || ""), "g"),
          "[REDACTED]"
        );
        throw new Error(`Anthropic translation error: ${safeErrorMessage}`);
      }
      throw new Error("Unknown translation error occurred");
    }
  }
}
